import { all, put, race, select, takeEvery } from 'redux-saga/effects';
import { getFormValues, startSubmit, stopSubmit } from 'redux-form';
import { push } from 'connected-react-router';
import { notificationsAdd } from 'virtues-bootstrap';
import { getUniqId } from 'utilities';
import i18n from 'services/i18n';
import {
  addOrganizations,
  requestOrganizationsAddOrganization,
  requestOrganizationsAddCareTeam,
} from 'store/actionCreators/organizations';
import {
  fetchOrganization,
  initOrganizationEdit,
  requestOrganization,
  requestOrganizationEdit,
  initEditSuperCareteamUsers,
  requestEditSuperCareteamUsers,
} from 'store/actionCreators/organization';
import { FORM_NAMES } from 'constants/forms';
import { createSagaRequestRace, createSagaRace } from 'utilities/request';
import {
  createFhirCareTeamObject,
  createFhirOrgnaizationObject,
} from 'utilities/fhir/organizations';
import { requestPractitioners } from 'store/actionCreators/practitioners';
import { getPractitionersEntry, handlePractitionersBySite } from 'store/selectors/practitioners';
import {
  getSiteId,
  getOrganizationId,
  getUpdatedSuperCareteam,
} from 'store/selectors/organization';

export function* onOrganizationsAdd() {
  yield put(startSubmit(FORM_NAMES.addOrganization));
  const practitioners = yield select(getPractitionersEntry);
  const values = yield select(getFormValues(FORM_NAMES.addOrganization));
  const organizationObject = createFhirOrgnaizationObject(values);
  const careTeamObject = createFhirCareTeamObject(values, practitioners);

  yield put(
    requestOrganizationsAddOrganization({ siteId: values.siteId, data: organizationObject }),
  );
  yield put(requestOrganizationsAddCareTeam({ siteId: values.siteId, data: careTeamObject }));

  yield all({
    addOrganization: race(createSagaRequestRace(requestOrganizationsAddOrganization)),
    addCareTeam: race(createSagaRequestRace(requestOrganizationsAddCareTeam)),
  });

  yield put(stopSubmit(FORM_NAMES.addOrganization));
  yield put(push('/'));
}

export function* handleFetchOrganization() {
  const siteId = yield select(getSiteId);
  const regSiteId = `${siteId.match(/\d{2,}/)[0]}-careteam`;

  yield put(requestPractitioners());

  const { failure } = yield createSagaRace(requestPractitioners);

  if (failure) {
    yield put(push('/'));
    return;
  }

  yield put(requestOrganization({ id: regSiteId }));
}

export function* handleEditOrganization() {
  try {
    yield put(startSubmit(FORM_NAMES.addOrganization));

    const careTeamId = yield select(getSiteId);
    const orgId = yield select(getOrganizationId);
    const { participants } = yield select(getFormValues(FORM_NAMES.addOrganization));
    const practitioners = yield select(getPractitionersEntry);

    const activeParticipants = Object.keys(participants).filter(
      participantId => participants[participantId],
    );
    const handledPractitioners = handlePractitionersBySite(practitioners);

    const data = {
      practitioners: activeParticipants.map(participantId => handledPractitioners[participantId]),
    };

    yield put(requestOrganizationEdit({ orgId, careTeamId, data }));

    const { success } = yield createSagaRace(requestOrganizationEdit);

    if (success) {
      yield put(stopSubmit(FORM_NAMES.addOrganization));
      yield put(push('/'));
    }
  } catch (error) {
    yield put(stopSubmit(FORM_NAMES.addOrganization));

    yield put(
      notificationsAdd({
        id: getUniqId(),
        variant: 'danger',
        title: i18n.t('common:requestError'),
        description: error.toString(),
      }),
    );
  }
}

export function* handleEditSuperCareTeam() {
  try {
    yield put(startSubmit(FORM_NAMES.addOrganization));

    const careTeamId = yield select(getSiteId);
    const { participants } = yield select(getFormValues(FORM_NAMES.addOrganization));
    const practitioners = yield select(getPractitionersEntry);

    const activeParticipants = Object.keys(participants).filter(
      participantId => participants[participantId],
    );

    const handledPractitioners = handlePractitionersBySite(practitioners);

    const newParticipants = activeParticipants.map(participantId => ({
      member: handledPractitioners[participantId],
    }));

    const data = yield select(getUpdatedSuperCareteam(newParticipants));

    yield put(requestEditSuperCareteamUsers({ careTeamId, data }));

    const { success } = yield createSagaRace(requestEditSuperCareteamUsers);

    if (success) {
      yield put(stopSubmit(FORM_NAMES.addOrganization));
      yield put(push('/'));
    }
  } catch (error) {
    yield put(stopSubmit(FORM_NAMES.addOrganization));

    yield put(
      notificationsAdd({
        id: getUniqId(),
        variant: 'danger',
        title: i18n.t('common:requestError'),
        description: error.toString(),
      }),
    );
  }
}

export default function*() {
  yield takeEvery(addOrganizations, onOrganizationsAdd);
  yield takeEvery(fetchOrganization, handleFetchOrganization);
  yield takeEvery(initOrganizationEdit, handleEditOrganization);
  yield takeEvery(initEditSuperCareteamUsers, handleEditSuperCareTeam);
}
